import type {
  ChartArea,
  ChartEventWindow,
  ChartLayer,
  ChartMarker,
  ChartReferenceBand,
  ChartReferenceLine,
  ChartSeries,
  ChartSpec,
  ChartStyle,
} from './types';

type LayeredChartObject = {
  id: string;
  label?: string;
  layerId?: string;
  groupId?: string;
  style?: ChartStyle;
};

export function chartObjectLayerId(kind: string, item: LayeredChartObject): string {
  return item.layerId || item.groupId || `${kind}_${item.id}`;
}

function representativeStyle(
  members: LayeredChartObject[],
  fallback?: ChartStyle,
): ChartStyle | undefined {
  return members.find((member) => member.style?.color || member.style?.fill)?.style || fallback;
}

export function chartAreas(spec: ChartSpec): ChartArea[] {
  return [
    ...(spec.areas || []),
    ...(spec.referenceBands || []).map((band: ChartReferenceBand) => ({
      ...band,
      layerId: band.layerId || band.groupId,
    })),
    ...(spec.eventWindows || []).map((window: ChartEventWindow) => ({
      ...window,
      layerId: window.layerId || window.groupId,
    })),
  ];
}

export function chartLayers(spec: ChartSpec): ChartLayer[] {
  const declared = new Map<string, ChartLayer>();
  (spec.layers || []).forEach((layer) => declared.set(layer.id, layer));
  (spec.overlayGroups || []).forEach((group) => {
    if (!declared.has(group.id)) {
      declared.set(group.id, {
        ...group,
        kind: 'indicator',
        description: `${group.label} chart objects`,
      });
    }
  });

  const collections: Array<[string, LayeredChartObject[], ChartLayer['kind']]> = [
    ['series', spec.series || [], 'data'],
    ['line', spec.referenceLines || [], 'annotation'],
    ['area', chartAreas(spec), 'area'],
    ['marker', spec.markers || [], 'marker'],
    ['marker_dataset', spec.markerDatasets || [], 'marker'],
  ];
  const membersByLayer = new Map<string, LayeredChartObject[]>();
  collections.forEach(([kind, members]) => {
    members.forEach((member) => {
      const id = chartObjectLayerId(kind, member);
      const layerMembers = membersByLayer.get(id);
      if (layerMembers) layerMembers.push(member);
      else membersByLayer.set(id, [member]);
    });
  });
  collections.forEach(([kind, members, layerKind]) => {
    members.forEach((member) => {
      const id = chartObjectLayerId(kind, member);
      if (declared.has(id)) return;
      const siblingMembers = members.filter(
        (candidate) => chartObjectLayerId(kind, candidate) === id,
      );
      const label = member.label || (kind === 'area' ? 'Analytical area' : `${kind} ${member.id}`);
      declared.set(id, {
        id,
        label,
        kind: layerKind,
        defaultVisible: true,
        description: `${label} ${layerKind || 'chart'} layer`,
        style: representativeStyle(siblingMembers),
      });
    });
  });
  return [...declared.values()].map((layer) => ({
    ...layer,
    style: layer.style || representativeStyle(membersByLayer.get(layer.id) || []),
  }));
}
